// src/components/CyberNewsView.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Newspaper, Search, ExternalLink, Bookmark, BookmarkCheck, Pin, PinOff,
  Star, StarOff, EyeOff, RefreshCw, Filter, ChevronDown, Tag, Shield,
  Bug, Users, AlertTriangle, Clock, TrendingUp, GripVertical, X,
  Rss, Plus, ToggleLeft, ToggleRight, Globe, Zap, Archive
} from 'lucide-react';
import DarkWebIntelTab from './DarkWebIntelTab';
const CyberNewsView = ({ isSuperAdmin }) => {
  const { getToken, isSignedIn } = useAuth();

  // ---- State ----
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [stats, setStats] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showBookmarked, setShowBookmarked] = useState(false);
  const [showPinned, setShowPinned] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Sources (for filter dropdown + admin)
  const [sources, setSources] = useState([]);
  const [showSourceManager, setShowSourceManager] = useState(false);

  // Add Source modal
  const [showAddSource, setShowAddSource] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [newSourceCategory, setNewSourceCategory] = useState('general');

  // Active view tab
  const [activeTab, setActiveTab] = useState('feed'); // feed, bookmarks, dashboard

  // Resizable panel
  const [leftPanelWidth, setLeftPanelWidth] = useState(440);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);

  const CATEGORIES = ['general', 'vulnerability', 'malware', 'breach', 'policy', 'vendor'];

  // ---- Data Fetching ----
  const fetchArticles = useCallback(async () => {
    if (!isSignedIn) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '30',
        sort: sortBy,
      });
      if (searchTerm) params.set('search', searchTerm);
      if (filterSource) params.set('source', filterSource);
      if (filterCategory) params.set('category', filterCategory);
      if (filterTag) params.set('tag', filterTag);
      if (showBookmarked) params.set('bookmarked', 'true');
      if (showPinned) params.set('pinned', 'true');

      const res = await fetch(`/api/cyber-news?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`Failed to load news (${res.status})`);
      const data = await res.json();
      setArticles(data.articles || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.total || 0);
      if (data.articles?.length > 0 && !selectedArticle) {
        setSelectedArticle(data.articles[0]);
      }
    } catch (e) {
      console.error('News load error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, getToken, page, sortBy, searchTerm, filterSource, filterCategory, filterTag, showBookmarked, showPinned, selectedArticle]);

  const fetchStats = useCallback(async () => {
    if (!isSignedIn) return;
    try {
      const token = await getToken();
      const res = await fetch('/api/cyber-news/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (e) {
      console.error('Stats load error:', e);
    }
  }, [isSignedIn, getToken]);

  const fetchSources = useCallback(async () => {
    if (!isSignedIn) return;
    try {
      const token = await getToken();
      const res = await fetch('/api/cyber-news/sources', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSources(await res.json());
    } catch (e) {
      console.error('Sources load error:', e);
    }
  }, [isSignedIn, getToken]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);
  useEffect(() => { fetchStats(); fetchSources(); }, [fetchStats, fetchSources]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [searchTerm, filterSource, filterCategory, filterTag, showBookmarked, showPinned, sortBy]);

  // ---- Actions ----
  const toggleBookmark = async (articleId) => {
    try {
      const token = await getToken();
      const res = await fetch('/api/cyber-news/bookmarks', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ news_id: articleId })
      });
      if (res.ok) {
        const data = await res.json();
        setArticles(prev => prev.map(a =>
          a.id === articleId ? { ...a, is_bookmarked: data.bookmarked } : a
        ));
        if (selectedArticle?.id === articleId) {
          setSelectedArticle(prev => ({ ...prev, is_bookmarked: data.bookmarked }));
        }
        fetchStats();
      }
    } catch (e) {
      console.error('Bookmark error:', e);
    }
  };

  const updateArticle = async (articleId, updates) => {
    try {
      const token = await getToken();
      const res = await fetch(`/api/cyber-news/${articleId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        const updated = await res.json();
        setArticles(prev => prev.map(a => a.id === articleId ? { ...a, ...updated } : a));
        if (selectedArticle?.id === articleId) {
          setSelectedArticle(prev => ({ ...prev, ...updated }));
        }
      }
    } catch (e) {
      console.error('Update error:', e);
    }
  };

  const triggerFetch = async () => {
    try {
      const token = await getToken();
      await fetch('/api/cyber-news/fetch-now', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeout(() => { fetchArticles(); fetchStats(); }, 5000);
    } catch (e) {
      console.error('Trigger fetch error:', e);
    }
  };

  const addSource = async () => {
    if (!newSourceName || !newSourceUrl) return;
    try {
      const token = await getToken();
      const res = await fetch('/api/cyber-news/sources', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newSourceName, url: newSourceUrl, category: newSourceCategory })
      });
      if (res.ok) {
        fetchSources();
        setShowAddSource(false);
        setNewSourceName('');
        setNewSourceUrl('');
        setNewSourceCategory('general');
      }
    } catch (e) {
      console.error('Add source error:', e);
    }
  };

  const toggleSource = async (sourceId, isActive) => {
    try {
      const token = await getToken();
      await fetch(`/api/cyber-news/sources/${sourceId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !isActive })
      });
      fetchSources();
    } catch (e) {
      console.error('Toggle source error:', e);
    }
  };

  // ---- Resizer ----
  const handleMouseDown = (e) => { e.preventDefault(); setIsResizing(true); };
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - rect.left;
      if (newWidth >= 300 && newWidth <= rect.width * 0.65) setLeftPanelWidth(newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // ---- Helpers ----
  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const getTagIcon = (type) => {
    switch (type) {
      case 'cve': return <Shield className="w-3 h-3" />;
      case 'threat_actor': return <Users className="w-3 h-3" />;
      case 'malware': return <Bug className="w-3 h-3" />;
      case 'topic': return <Tag className="w-3 h-3" />;
      default: return <Tag className="w-3 h-3" />;
    }
  };

  const getTagColor = (type) => {
    switch (type) {
      case 'cve': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'threat_actor': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'malware': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'topic': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getCategoryBadge = (cat) => {
    const colors = {
      general: 'bg-blue-500/20 text-blue-400',
      vulnerability: 'bg-red-500/20 text-red-400',
      malware: 'bg-purple-500/20 text-purple-400',
      breach: 'bg-orange-500/20 text-orange-400',
      policy: 'bg-green-500/20 text-green-400',
      vendor: 'bg-cyan-500/20 text-cyan-400'
    };
    return colors[cat] || colors.general;
  };

  const parseTags = (tags) => {
    if (!tags) return [];
    if (typeof tags === 'string') {
      try { return JSON.parse(tags); } catch { return []; }
    }
    return Array.isArray(tags) ? tags : [];
  };

  // ===============================================
  // RENDER
  // ===============================================
  return (
    <div className="flex flex-col h-full bg-slate-950" ref={containerRef}>
      {/* Header Bar */}
      <div className="flex-shrink-0 border-b border-slate-800/60 bg-slate-900/40 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Newspaper className="w-5 h-5 text-cyan-400" />
            <h1 className="text-lg font-semibold text-white">Cybersecurity News</h1>
            {stats && (
              <span className="text-xs text-slate-500 ml-2">
                {stats.today_count} today · {stats.week_count} this week
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* View Tabs */}
            <div className="flex bg-slate-800/50 rounded-lg p-0.5 mr-3">
              {[
                { id: 'feed', label: 'Feed', icon: Rss },
                { id: 'bookmarks', label: 'Bookmarks', icon: Bookmark },
                { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
                { id: 'darkweb', label: 'Dark Web Intel', icon: AlertTriangle },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    if (tab.id === 'bookmarks') { setShowBookmarked(true); setShowPinned(false); }
                    else if (tab.id === 'feed') { setShowBookmarked(false); setShowPinned(false); }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : 'text-slate-400 hover:text-slate-300'
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  {tab.label}
                  {tab.id === 'bookmarks' && stats?.bookmark_count > 0 && (
                    <span className="bg-cyan-500/30 text-cyan-300 text-[10px] px-1.5 rounded-full ml-1">
                      {stats.bookmark_count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {isSuperAdmin && (
              <>
                <button
                  onClick={() => setShowSourceManager(!showSourceManager)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-800/60 text-slate-300 rounded-lg hover:bg-slate-700/60 transition-all border border-slate-700/40"
                >
                  <Rss className="w-3.5 h-3.5" />
                  Sources
                </button>
                <button
                  onClick={triggerFetch}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-cyan-600/20 text-cyan-400 rounded-lg hover:bg-cyan-600/30 transition-all border border-cyan-500/30"
                  title="Refresh feeds now"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Fetch Now
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Source Manager Panel (Super Admin) */}
      {showSourceManager && isSuperAdmin && (
        <div className="flex-shrink-0 border-b border-slate-800/60 bg-slate-900/30 px-5 py-3 max-h-64 overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-white flex items-center gap-2">
              <Rss className="w-4 h-4 text-cyan-400" />
              RSS Feed Sources
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddSource(!showAddSource)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-cyan-600/20 text-cyan-400 rounded hover:bg-cyan-600/30 border border-cyan-500/30"
              >
                <Plus className="w-3 h-3" /> Add Source
              </button>
              <button onClick={() => setShowSourceManager(false)} className="text-slate-500 hover:text-slate-300">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showAddSource && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-slate-800/40 rounded-lg border border-slate-700/40">
              <input
                value={newSourceName}
                onChange={e => setNewSourceName(e.target.value)}
                placeholder="Source name"
                className="flex-1 px-2 py-1.5 text-xs bg-slate-900/50 border border-slate-700/50 rounded text-white placeholder-slate-500"
              />
              <input
                value={newSourceUrl}
                onChange={e => setNewSourceUrl(e.target.value)}
                placeholder="RSS Feed URL"
                className="flex-[2] px-2 py-1.5 text-xs bg-slate-900/50 border border-slate-700/50 rounded text-white placeholder-slate-500"
              />
              <select
                value={newSourceCategory}
                onChange={e => setNewSourceCategory(e.target.value)}
                className="px-2 py-1.5 text-xs bg-slate-900/50 border border-slate-700/50 rounded text-white"
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={addSource} className="px-3 py-1.5 text-xs bg-cyan-600 text-white rounded hover:bg-cyan-500">
                Add
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
            {sources.map(s => (
              <div key={s.id} className="flex items-center justify-between p-2 bg-slate-800/30 rounded border border-slate-700/30">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-white truncate">{s.name}</div>
                  <div className="text-[10px] text-slate-500">
                    {s.article_count || 0} articles · {s.category}
                    {s.last_error && <span className="text-red-400 ml-1">⚠ Error</span>}
                  </div>
                </div>
                <button
                  onClick={() => toggleSource(s.id, s.is_active)}
                  className="ml-2 flex-shrink-0"
                  title={s.is_active ? 'Disable source' : 'Enable source'}
                >
                  {s.is_active
                    ? <ToggleRight className="w-5 h-5 text-green-400" />
                    : <ToggleLeft className="w-5 h-5 text-slate-600" />
                  }
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DASHBOARD VIEW */}
      {activeTab === 'dashboard' && stats && (
        <div className="flex-1 overflow-y-auto p-5">
          <div className="max-w-6xl mx-auto space-y-5">
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Today', value: stats.today_count, icon: Clock, color: 'cyan' },
                { label: 'This Week', value: stats.week_count, icon: TrendingUp, color: 'blue' },
                { label: 'Bookmarks', value: stats.bookmark_count, icon: Bookmark, color: 'yellow' },
                { label: 'Sources', value: sources.filter(s => s.is_active).length, icon: Rss, color: 'green' },
              ].map((stat, i) => (
                <div key={i} className="bg-slate-900/50 border border-slate-800/60 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <stat.icon className={`w-5 h-5 text-${stat.color}-400`} />
                    <span className="text-2xl font-bold text-white">{stat.value}</span>
                  </div>
                  <div className="text-xs text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-5">
              {/* Trending Tags */}
              <div className="bg-slate-900/50 border border-slate-800/60 rounded-xl p-4">
                <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-cyan-400" />
                  Trending This Week
                </h3>
                <div className="space-y-2">
                  {(stats.trending_tags || []).map((t, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <button
                        onClick={() => { setFilterTag(t.tag); setActiveTab('feed'); setShowBookmarked(false); }}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs border ${getTagColor(t.type)} hover:opacity-80 transition-all`}
                      >
                        {getTagIcon(t.type)}
                        {t.tag}
                      </button>
                      <span className="text-xs text-slate-500">{t.mentions} mentions</span>
                    </div>
                  ))}
                  {(!stats.trending_tags || stats.trending_tags.length === 0) && (
                    <p className="text-xs text-slate-500 italic">No trending tags this week</p>
                  )}
                </div>
              </div>

              {/* Top Sources */}
              <div className="bg-slate-900/50 border border-slate-800/60 rounded-xl p-4">
                <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-cyan-400" />
                  Most Active Sources
                </h3>
                <div className="space-y-2">
                  {(stats.top_sources || []).map((s, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <button
                        onClick={() => { setFilterSource(s.source_name); setActiveTab('feed'); setShowBookmarked(false); }}
                        className="text-xs text-slate-300 hover:text-cyan-400 transition-colors"
                      >
                        {s.source_name}
                      </button>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-cyan-500/60 rounded-full"
                            style={{ width: `${Math.min(100, (parseInt(s.count) / Math.max(...stats.top_sources.map(x => parseInt(x.count)))) * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 w-8 text-right">{s.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Latest Headlines */}
            <div className="bg-slate-900/50 border border-slate-800/60 rounded-xl p-4">
              <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-400" />
                Latest Headlines
              </h3>
              <div className="space-y-3">
                {(stats.latest || []).map((article, i) => (
                  <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-800/30 transition-colors">
                    <div className="flex-1 min-w-0">
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-slate-200 hover:text-cyan-400 transition-colors line-clamp-1 block"
                      >
                        {article.title}
                      </a>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-slate-500">{article.source_name}</span>
                        <span className="text-[10px] text-slate-600">·</span>
                        <span className="text-[10px] text-slate-500">{timeAgo(article.published_at)}</span>
                        {article.mentioned_cves?.length > 0 && (
                          <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 rounded border border-red-500/20">
                            {article.mentioned_cves[0]}{article.mentioned_cves.length > 1 ? ` +${article.mentioned_cves.length - 1}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    {article.relevance_score > 0 && (
                      <div className="flex-shrink-0 text-[10px] text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">
                        {article.relevance_score}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DARK WEB INTEL TAB */}
      {activeTab === 'darkweb' && <DarkWebIntelTab />}

      {/* FEED / BOOKMARKS VIEW (Split Panel) */}
      {(activeTab === 'feed' || activeTab === 'bookmarks') && (
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT PANEL - Article List */}
          <div className="flex flex-col border-r border-slate-800/60" style={{ width: leftPanelWidth, minWidth: 300 }}>
            {/* Filter Bar */}
            <div className="flex-shrink-0 p-3 border-b border-slate-800/40 space-y-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search articles..."
                    className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-900/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none"
                  />
                </div>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="px-2 py-1.5 text-xs bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-300"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="relevance">Relevance</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={filterSource}
                  onChange={e => setFilterSource(e.target.value)}
                  className="flex-1 px-2 py-1 text-[11px] bg-slate-900/50 border border-slate-700/50 rounded text-slate-300"
                >
                  <option value="">All Sources</option>
                  {sources.filter(s => s.is_active).map(s => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
                <select
                  value={filterCategory}
                  onChange={e => setFilterCategory(e.target.value)}
                  className="flex-1 px-2 py-1 text-[11px] bg-slate-900/50 border border-slate-700/50 rounded text-slate-300"
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </div>
              {filterTag && (
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-slate-500">Tag filter:</span>
                  <span className="flex items-center gap-1 text-[11px] bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded border border-cyan-500/30">
                    {filterTag}
                    <button onClick={() => setFilterTag('')} className="hover:text-white ml-1"><X className="w-3 h-3" /></button>
                  </span>
                </div>
              )}
            </div>

            {/* Article List */}
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
                  <span className="ml-2 text-sm text-slate-400">Loading news...</span>
                </div>
              )}
              {error && (
                <div className="m-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
                  {error}
                </div>
              )}
              {!loading && articles.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <Newspaper className="w-8 h-8 mb-2 opacity-30" />
                  <p className="text-sm">No articles found</p>
                  <p className="text-xs mt-1">Try adjusting your filters or wait for the next feed sync</p>
                </div>
              )}

              {articles.map(article => {
                const tags = parseTags(article.tags);
                const isSelected = selectedArticle?.id === article.id;
                return (
                  <div
                    key={article.id}
                    onClick={() => setSelectedArticle(article)}
                    className={`px-4 py-3 border-b border-slate-800/30 cursor-pointer transition-all hover:bg-slate-800/30 ${
                      isSelected ? 'bg-slate-800/50 border-l-2 border-l-cyan-500' : 'border-l-2 border-l-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className={`text-xs font-medium leading-snug line-clamp-2 ${
                        isSelected ? 'text-cyan-300' : 'text-slate-200'
                      }`}>
                        {article.is_pinned && <Pin className="w-3 h-3 inline mr-1 text-yellow-400" />}
                        {article.is_featured && <Star className="w-3 h-3 inline mr-1 text-yellow-400" />}
                        {article.title}
                      </h3>
                      {article.is_bookmarked && (
                        <BookmarkCheck className="w-3.5 h-3.5 flex-shrink-0 text-cyan-400 mt-0.5" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${getCategoryBadge(article.category)}`}>
                        {article.source_name}
                      </span>
                      <span className="text-[10px] text-slate-500">{timeAgo(article.published_at)}</span>
                      {article.relevance_score > 0 && (
                        <span className="text-[10px] text-cyan-500 bg-cyan-500/10 px-1 rounded">
                          ↑{article.relevance_score}
                        </span>
                      )}
                    </div>
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {tags.slice(0, 4).map((tag, i) => (
                          <span key={i} className={`flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded border ${getTagColor(tag.type)}`}>
                            {getTagIcon(tag.type)}
                            {tag.value}
                          </span>
                        ))}
                        {tags.length > 4 && (
                          <span className="text-[9px] text-slate-500 px-1">+{tags.length - 4}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800/40">
                  <span className="text-[10px] text-slate-500">{totalCount} articles</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="px-2 py-1 text-[10px] text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      ← Prev
                    </button>
                    <span className="text-[10px] text-slate-500">{page} / {totalPages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="px-2 py-1 text-[10px] text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Resizer */}
          <div
            className="w-1 bg-slate-800/40 hover:bg-cyan-500/40 cursor-col-resize transition-colors flex items-center justify-center"
            onMouseDown={handleMouseDown}
          >
            <GripVertical className="w-3 h-3 text-slate-600" />
          </div>

          {/* RIGHT PANEL - Article Detail */}
          <div className="flex-1 overflow-y-auto bg-slate-950/50">
            {selectedArticle ? (
              <div className="p-6">
                {/* Admin Actions */}
                {isSuperAdmin && (
                  <div className="flex items-center gap-2 mb-4">
                    <button
                      onClick={() => updateArticle(selectedArticle.id, { is_pinned: !selectedArticle.is_pinned })}
                      className={`flex items-center gap-1 px-2 py-1 text-[11px] rounded border transition-all ${
                        selectedArticle.is_pinned
                          ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                          : 'bg-slate-800/40 text-slate-400 border-slate-700/40 hover:text-yellow-400'
                      }`}
                    >
                      {selectedArticle.is_pinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
                      {selectedArticle.is_pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button
                      onClick={() => updateArticle(selectedArticle.id, { is_featured: !selectedArticle.is_featured })}
                      className={`flex items-center gap-1 px-2 py-1 text-[11px] rounded border transition-all ${
                        selectedArticle.is_featured
                          ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                          : 'bg-slate-800/40 text-slate-400 border-slate-700/40 hover:text-yellow-400'
                      }`}
                    >
                      {selectedArticle.is_featured ? <StarOff className="w-3 h-3" /> : <Star className="w-3 h-3" />}
                      {selectedArticle.is_featured ? 'Unfeature' : 'Feature'}
                    </button>
                    <button
                      onClick={() => updateArticle(selectedArticle.id, { is_hidden: true })}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] rounded border bg-slate-800/40 text-slate-400 border-slate-700/40 hover:text-red-400 hover:border-red-500/30 transition-all"
                    >
                      <EyeOff className="w-3 h-3" />
                      Hide
                    </button>
                  </div>
                )}

                {/* Article Header */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <h2 className="text-lg font-semibold text-white leading-snug">{selectedArticle.title}</h2>
                  <button
                    onClick={() => toggleBookmark(selectedArticle.id)}
                    className={`flex-shrink-0 p-2 rounded-lg border transition-all ${
                      selectedArticle.is_bookmarked
                        ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
                        : 'bg-slate-800/40 text-slate-400 border-slate-700/40 hover:text-cyan-400'
                    }`}
                    title={selectedArticle.is_bookmarked ? 'Remove bookmark' : 'Bookmark article'}
                  >
                    {selectedArticle.is_bookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                  </button>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-3 mb-4">
                  <span className={`text-xs px-2 py-1 rounded ${getCategoryBadge(selectedArticle.category)}`}>
                    {selectedArticle.source_name}
                  </span>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {selectedArticle.published_at ? new Date(selectedArticle.published_at).toLocaleString() : 'Unknown'}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${getCategoryBadge(selectedArticle.category)}`}>
                    {selectedArticle.category}
                  </span>
                  {selectedArticle.relevance_score > 0 && (
                    <span className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Relevance: {selectedArticle.relevance_score}
                    </span>
                  )}
                </div>

                {/* Tags */}
                {(() => {
                  const tags = parseTags(selectedArticle.tags);
                  if (tags.length === 0) return null;
                  return (
                    <div className="mb-4">
                      <h4 className="text-xs font-medium text-slate-400 mb-2">Detected Tags</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {tags.map((tag, i) => (
                          <button
                            key={i}
                            onClick={() => { setFilterTag(tag.value); setActiveTab('feed'); }}
                            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded border transition-all hover:opacity-80 ${getTagColor(tag.type)}`}
                          >
                            {getTagIcon(tag.type)}
                            {tag.value}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Description */}
                <div className="mb-6 p-4 bg-slate-900/40 rounded-xl border border-slate-800/50">
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                    {selectedArticle.description || 'No description available.'}
                  </p>
                </div>

                {/* Read Full Article */}
                <a
                  href={selectedArticle.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600/20 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-600/30 transition-all text-sm font-medium"
                >
                  <ExternalLink className="w-4 h-4" />
                  Read Full Article
                </a>

                {/* Related CVEs section - link to Vuln Intelligence */}
                {selectedArticle.mentioned_cves?.length > 0 && (
                  <div className="mt-6 p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
                    <h4 className="text-xs font-medium text-red-400 mb-2 flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5" />
                      Referenced CVEs
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.mentioned_cves.map((cve, i) => (
                        <span key={i} className="text-xs bg-red-500/20 text-red-300 px-2 py-1 rounded border border-red-500/20 font-mono">
                          {cve}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Referenced Threat Actors */}
                {selectedArticle.mentioned_threat_actors?.length > 0 && (
                  <div className="mt-4 p-4 bg-orange-500/5 border border-orange-500/20 rounded-xl">
                    <h4 className="text-xs font-medium text-orange-400 mb-2 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />
                      Referenced Threat Actors
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.mentioned_threat_actors.map((actor, i) => (
                        <span key={i} className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded border border-orange-500/20">
                          {actor}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Referenced Malware */}
                {selectedArticle.mentioned_malware?.length > 0 && (
                  <div className="mt-4 p-4 bg-purple-500/5 border border-purple-500/20 rounded-xl">
                    <h4 className="text-xs font-medium text-purple-400 mb-2 flex items-center gap-1.5">
                      <Bug className="w-3.5 h-3.5" />
                      Referenced Malware
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.mentioned_malware.map((mal, i) => (
                        <span key={i} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded border border-purple-500/20">
                          {mal}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <Newspaper className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-sm">Select an article to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CyberNewsView;